// §4.4's FIFTH BUTTON — Guided cut (slice 07-C; D-158 / D-167 / D-181 / D-182).
//
// 07-B-3 shipped the failure-action sheet with FOUR actions and recorded the
// fifth's ABSENCE as a decision: "an unbuilt slice is not a control". The slice
// is built, so the control exists — and every fact it renders comes from the
// server's `guided_cut` block on the inspection read. NOT ONE OF THEM IS
// RE-DERIVED HERE, and that is the whole point of this file:
//
//   D-181 exists because the SERVER held two implementations of "is a cut
//   allowed" — the eligibility block asked six questions and `propose` asked
//   seven — so the block published `eligible: true` on inspections the propose
//   route then refused 422, i.e. a LIVE control that fails on tap, in the module
//   that argues hardest against dead controls. The repair was to make the block
//   call the propose's own planner. A CLIENT copy of that judgement would be the
//   third implementation and would drift the same way, offline, where nothing
//   can catch it.
//
// So: `eligible` decides live, `reason` decides which sentence, and this file
// contains no arithmetic about cables at all.
//
// ── THE RENDER CLASSES, and why there are four rather than two ──────────────
//
// D-118 has two halves and §4.4/§4.5 apply both to this one control:
//
//   ABSENT   — the principal holds no `quality.cut.execute` (authority decides
//              EXISTENCE, exactly as the other four buttons behave), or the
//              cut is not an action on this check at all
//              (`not_offered_by_control_point`, `subject_not_length_measured`).
//              A dead control in either case would imply a cut exists here.
//   DEAD + WHY — the control stays and carries its reason INSIDE the slab.
//              D-167's argument, stated once and applying to all five of these:
//              depth, drum length, lifecycle, a missing fault mark and a plan
//              that keeps nothing are NOT permission facts, and an absence
//              would read — to a qc lead who HOLDS the grant and saw the button
//              on the previous bobbin — as a refusal of their authority.
//   DEAD + WHY + THE FIX — `length_not_measured` and `fault_not_on_source`,
//              whose fix is the same re-measure, offered HERE and never as a
//              link away: "a link throws away the form the operator is standing
//              in" (§4.4).
//   LIVE     — a link to §4.5's cut screen. `eligible: true` is a promise that
//              `POST /inspections/{id}/cut-events` with an empty body succeeds
//              (D-181 pt 3), which is exactly what the screen sends.
//
// ── THE TENTH CLASS, WHICH IS NOT A SPEC REASON ─────────────────────────────
//
// `unknown`. The floor's inspection read is a `useCachedRead` persisted in
// `assessly.floor.read.v1:` — a key with NO schema version — so a device that
// cached an inspection BEFORE 07-C shipped holds a payload with no `guided_cut`
// at all. Reading `.eligible` off it throws and white-screens the execution
// screen, offline, which is the one place this product may not fail. The block
// is therefore optional at this boundary and its absence is a stated fact with
// its own next step ("reconnect and open the check again") rather than a
// silent absence that would read as a refusal. The same class absorbs a
// contract violation (`eligible: false` with `reason: null`): both mean "this
// client cannot tell", and both are fixed by a fresh read.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Button } from "../../../../components/button";
import { formatMeasure } from "../../inventory/lib/measure";
import { isExactMeasurement } from "../lib/grading";
import type { QualityGuidedCutReason, QualityInspectionGuidedCut } from "../hooks/quality-queries";
import type { FailureActionArm } from "./inspection-run-port";

/** How the §4.4 sheet renders the guided-cut control. Pure data, so the
 * decision is unit-testable without a DOM and cannot be re-spelled inside a
 * JSX condition. */
export type GuidedCutRender =
  | { readonly kind: "absent" }
  | { readonly kind: "live" }
  | {
      readonly kind: "dead";
      /** `undefined` for the tenth class (see the header) — the block itself is
       * missing or self-contradictory, so there is no spec reason to name. */
      readonly reason: QualityGuidedCutReason | undefined;
      /** Which in-place fix the slab carries beside the sentence. */
      readonly fix: "none" | "remeasure";
    };

/** The two reasons that mean "there is no cut here and never was" (§4.4's
 * second bullet). Absence is the honest rendering: a dead control implies a
 * capability that does not exist on this check. */
const ABSENT_REASONS: ReadonlySet<string> = new Set([
  "not_offered_by_control_point",
  "subject_not_length_measured",
]);

/** The two reasons that are dead for a FIXABLE reason, and the fix is the same
 * re-measure in both (§9.6, §4.4's fourth and seventh bullets). */
const REMEASURE_REASONS: ReadonlySet<string> = new Set([
  "length_not_measured",
  "fault_not_on_source",
]);

/**
 * The one decision, taken once (pure).
 *
 * `block` is optional deliberately — see the header's tenth-class note. Order
 * matters and is the D-118 order: AUTHORITY decides existence before anything
 * else is even asked, because a principal without the grant must not learn the
 * drum's genealogy depth from a sentence they were never meant to see.
 */
export function guidedCutRender(input: {
  readonly canCut: boolean;
  readonly block: QualityInspectionGuidedCut | undefined;
}): GuidedCutRender {
  if (!input.canCut) return { kind: "absent" };
  const block = input.block;
  if (block === undefined) return { kind: "dead", reason: undefined, fix: "none" };
  if (block.eligible) return { kind: "live" };
  const reason = block.reason;
  if (reason === null) return { kind: "dead", reason: undefined, fix: "none" };
  if (ABSENT_REASONS.has(reason)) return { kind: "absent" };
  return {
    kind: "dead",
    reason,
    fix: REMEASURE_REASONS.has(reason) ? "remeasure" : "none",
  };
}

/**
 * The SENTENCE for a dead control, chosen from the server's `reason` and filled
 * from the server's own numbers.
 *
 * Two reasons take a second spelling when `min_sellable_length_m` is NULL,
 * which D-172 makes a real and different world: a control point that names no
 * minimum imposes none, so "both under the 10 m minimum" is not a true sentence
 * about that tenant's cut and the arithmetic the operator would check against
 * their tape measure is a different arithmetic. One key per WORLD, never one
 * key with a blank in it.
 */
export function guidedCutReasonKey(
  reason: QualityGuidedCutReason | undefined,
  block: QualityInspectionGuidedCut | undefined,
): string {
  if (reason === undefined) return "quality.run.sheet.cut.why.unknown";
  const noMinimum = block?.min_sellable_length_m === null;
  if (reason === "source_too_short_to_cut") {
    return noMinimum
      ? "quality.run.sheet.cut.why.source_too_short_to_cut_noMinimum"
      : "quality.run.sheet.cut.why.source_too_short_to_cut";
  }
  if (reason === "no_sellable_segment") {
    return noMinimum
      ? "quality.run.sheet.cut.why.no_sellable_segment_noMinimum"
      : "quality.run.sheet.cut.why.no_sellable_segment";
  }
  return `quality.run.sheet.cut.why.${reason}`;
}

/** The numbers a reason sentence quotes, rendered as EXACT decimals through the
 * scaled-integer formatter every measured length in this product goes through
 * (D-041/D-042) — never `Number()`, which would turn 1543.500250 into a
 * different drum. A missing value renders as an em dash rather than a
 * fabricated 0, for §9.10's reason: unknown is not zero.
 *
 * Exported because §4.5's screen renders the SAME sentences from the SAME
 * block when somebody opens the cut screen on an ineligible inspection. Two
 * surfaces, one verdict, one set of numbers — a second copy would eventually
 * quote a different trim than the button that sent the operator there. */
export function guidedCutReasonValues(
  block: QualityInspectionGuidedCut | undefined,
  locale: string,
): Record<string, string> {
  const m = (text: string | null | undefined): string =>
    text === null || text === undefined ? "—" : formatMeasure(text, { locale });
  return {
    length: m(block?.source_length_m),
    trim: m(block?.trim_allowance_m),
    minimum: m(block?.min_sellable_length_m),
    depth: block?.genealogy_depth === null ? "—" : String(block?.genealogy_depth ?? "—"),
  };
}

export function GuidedCutAction({
  render,
  block,
  inspectionId,
  stockUnitId,
  arm,
  canRemeasure,
  onApplied,
}: {
  readonly render: GuidedCutRender;
  readonly block: QualityInspectionGuidedCut | undefined;
  readonly inspectionId: string;
  /** The inspection's subject unit — the re-measure's target. Absent on a lot
   * or operation subject, which is also a subject no cut is offered on. */
  readonly stockUnitId: string | null;
  readonly arm: FailureActionArm;
  /** `inventory.scan.locate` — module 06's own code on its own route. Without
   * it the fix is ABSENT from the slab (the sentence stays: the reader still
   * needs to know a re-measure is what unblocks this) rather than a control
   * that 403s. */
  readonly canRemeasure: boolean;
  readonly onApplied?: (() => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const [fixOpen, setFixOpen] = useState(false);
  const [entered, setEntered] = useState("");
  const [fixOutcome, setFixOutcome] = useState<
    | { readonly ok: true; readonly remaining: string }
    | { readonly ok: false; readonly message: string }
    | undefined
  >(undefined);

  if (render.kind === "absent") return null;

  // OFFLINE OUTRANKS ELIGIBLE, and it is the same rule the other four controls
  // obey (§4.4's dated sentence, extended to the cut by D-166 with the argument
  // "every word of that applies here with more force"): a cut consumes a
  // physical unit, mints two, posts a waste row and closes an inspection, and
  // there is no un-cutting a cable. A live LINK offline would carry the
  // operator to a screen whose every control is dead — a dead end one tap
  // further away from the sentence that explains it.
  if (render.kind === "live" && !arm.online) {
    return (
      <button
        type="button"
        className="qrun-act qrun-act-plain"
        disabled
        data-testid="qrun-act-guided_cut"
      >
        <span className="qrun-act-label">{t("quality.run.sheet.act.guided_cut")}</span>
        <span className="qrun-act-reason" data-testid="qrun-act-guided_cut-reason">
          {t("quality.run.sheet.offline")}
        </span>
      </button>
    );
  }

  if (render.kind === "live") {
    // A LINK, not a button, and the shape says so (floor-inventory.css's own
    // rule: a button commits work, a link moves the screen). Nothing is written
    // by tapping this — §4.5's screen is where the propose happens, because the
    // propose's refusals need the surface that can re-plan and the 409
    // length-changed path needs somewhere to land.
    return (
      <Link
        to="/floor/quality/$id/cut"
        params={{ id: inspectionId }}
        className="qrun-act qrun-act-go"
        data-testid="qrun-act-guided_cut"
      >
        <span className="qrun-act-label">{t("quality.run.sheet.act.guided_cut")}</span>
        <span className="qrun-act-reason">{t("quality.run.sheet.cut.goHint")}</span>
      </Link>
    );
  }

  const offline = !arm.online;
  const showFix = render.fix === "remeasure" && canRemeasure && !offline;
  const measureValid = entered.trim().length > 0 && isExactMeasurement(entered.trim());

  return (
    <div className="qrun-act-dead" data-testid="qrun-act-guided_cut-dead">
      <button
        type="button"
        className="qrun-act qrun-act-plain"
        disabled
        data-testid="qrun-act-guided_cut"
      >
        <span className="qrun-act-label">{t("quality.run.sheet.act.guided_cut")}</span>
        {/* D-118, in its strictest form: the reason is INSIDE the slab, so it
            is part of the accessible name and cannot be scrolled away from the
            control it belongs to. */}
        <span className="qrun-act-reason" data-testid="qrun-act-guided_cut-reason">
          {t(guidedCutReasonKey(render.reason, block), guidedCutReasonValues(block, i18n.language))}
        </span>
      </button>

      {/* THE FIX, IN PLACE (§4.4). Offered only for the two reasons a
          re-measure clears, only to a principal holding module 06's own code,
          and only online — a re-measure that queued would leave the operator
          watching the same dead control with no way to know whether it had
          landed (the port's note). */}
      {render.fix === "remeasure" && !canRemeasure ? (
        <p className="qrun-note" data-testid="qrun-cut-fix-refused">
          {t("quality.run.sheet.cut.fix.noGrant")}
        </p>
      ) : null}
      {render.fix === "remeasure" && canRemeasure && offline ? (
        <p className="qrun-note" data-testid="qrun-cut-fix-offline">
          {t("quality.run.sheet.cut.fix.offline")}
        </p>
      ) : null}

      {showFix && stockUnitId !== null ? (
        fixOpen ? (
          <div className="qrun-sheet-form" data-testid="qrun-cut-fix-form">
            <label className="qrun-field">
              {t("quality.run.sheet.cut.fix.label")}
              <input
                inputMode="decimal"
                autoComplete="off"
                className="qrun-input"
                value={entered}
                data-testid="qrun-cut-fix-input"
                onChange={(e) => {
                  setEntered(e.target.value);
                  setFixOutcome(undefined);
                }}
              />
            </label>
            <Button
              variant="primary"
              data-testid="qrun-cut-fix-submit"
              disabled={!measureValid || arm.busy}
              busy={arm.busy}
              busyLabel={t("quality.run.saving")}
              onClick={() => {
                void (async () => {
                  const outcome = await arm.remeasure({
                    stockUnitId,
                    body: { entered_measure: entered.trim() },
                  });
                  if (outcome.kind === "done") {
                    setFixOutcome({ ok: true, remaining: outcome.result.remaining_measure });
                    setFixOpen(false);
                    setEntered("");
                  } else {
                    // The server's OWN sentence, relayed. Module 06's refusals
                    // here name the unit's status and its measure UoM, which is
                    // what the operator acts on.
                    setFixOutcome({ ok: false, message: outcome.message });
                  }
                  // Either way the block this control renders from may have
                  // changed — an applied re-measure flips `length_not_measured`
                  // the instant it lands (§9.6), and a refusal means the server
                  // holds a truth this screen does not.
                  onApplied?.();
                })();
              }}
            >
              {t("quality.run.sheet.cut.fix.submit")}
            </Button>
            {!measureValid && entered.trim().length > 0 ? (
              <p className="qrun-blocked" role="status" data-testid="qrun-cut-fix-invalid">
                {t("quality.run.measureInvalid")}
              </p>
            ) : null}
          </div>
        ) : (
          <Button
            size="sm"
            data-testid="qrun-cut-fix-open"
            onClick={() => {
              setFixOpen(true);
            }}
          >
            {t("quality.run.sheet.cut.fix.open")}
          </Button>
        )
      ) : null}

      {fixOutcome !== undefined ? (
        fixOutcome.ok ? (
          <p className="qrun-settle-ok" role="status" data-testid="qrun-cut-fix-done">
            {t("quality.run.sheet.cut.fix.done", {
              remaining: formatMeasure(fixOutcome.remaining, { locale: i18n.language }),
            })}
          </p>
        ) : (
          <p className="qrun-settle-bad" role="alert" data-testid="qrun-cut-fix-refused-server">
            {fixOutcome.message}
          </p>
        )
      ) : null}
    </div>
  );
}
